import { forwardRef, useRef, useState, useMemo } from 'react';
import type { PropsWithoutRef } from 'react';
import styled, { css } from 'styled-components';
import { mix } from 'polished';

import * as caretDownIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/caret-down.icon';
import * as paperClipIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/paper-clip.icon';
import {
  ExpandCollapse,
  Flex,
  Icon,
  registerIcon,
  Text,
  useI18n,
  defaultThemeProp,
  useAfterInitialEffect,
  DateTimeDisplay,
  readableColor,
  readableHue,
  useBreakpoint,
  useConfiguration,
  StyledIcon,
  tryCatch,
  useDirection,
  Status,
  getIntlDateTimeFormatFromCache,
  Count
} from '@pega/cosmos-react-core';
import type { ForwardRefForwardPropsComponent } from '@pega/cosmos-react-core';

import Email from './Email';
import type { EmailConversationProps, EmailProps } from './Email.types';

registerIcon(caretDownIcon, paperClipIcon);

const EmailParticipantTextCss = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

export const StyledEmailParticipantsText = styled(Text)`
  ${EmailParticipantTextCss}
`;

StyledEmailParticipantsText.defaultProps = defaultThemeProp;

export const StyledConversationHeader = styled.header<{ collapsed: boolean }>(
  ({
    theme: {
      base: { palette }
    },
    collapsed
  }) => {
    return css`
      border-bottom: ${collapsed ? 0 : '0.0625rem'} solid ${palette['border-line']};
      position: sticky;
      top: 0;
      z-index: 1;
    `;
  }
);
StyledConversationHeader.defaultProps = defaultThemeProp;

export const StyledUnReadCount = styled.div(
  ({
    theme: {
      base: {
        spacing,
        palette,
        'font-weight': { 'semi-bold': fontWeightSemiBold }
      }
    }
  }) => {
    const readableBackground = readableHue(palette['brand-primary'], palette['primary-background']);
    const readableTextColor = readableColor(readableBackground);

    return css`
      align-self: flex-start;
      background-color: ${readableBackground};
      color: ${readableTextColor};
      padding: 0 ${spacing};
      font-weight: ${fontWeightSemiBold};
      width: fit-content;
    `;
  }
);

StyledUnReadCount.defaultProps = defaultThemeProp;

export const StyledEmailConversation = styled.li<{
  singleConversation?: boolean;
  showHeader?: boolean;
}>(
  ({
    theme: {
      base: { spacing, palette, 'border-radius': baseBorderRadius }
    },
    singleConversation,
    showHeader
  }) => {
    return css`
      margin-bottom: ${spacing};
      border-radius: ${singleConversation && showHeader
        ? `0 0 ${baseBorderRadius} ${baseBorderRadius}`
        : baseBorderRadius};
      overflow: hidden;
      background-color: ${palette['primary-background']};
      list-style-type: none;
    `;
  }
);
StyledEmailConversation.defaultProps = defaultThemeProp;

export const StyledEmailInConversation = styled(Email)<Pick<EmailProps, 'unRead'>>(({
  unRead,
  theme: {
    base: { spacing, palette }
  }
}) => {
  const readableBackground = readableHue(palette['brand-primary'], palette['primary-background']);
  return css`
    padding: calc(2 * ${spacing}) 0;
    position: relative;
    &:not(:last-child) {
      border-bottom: 0.0625rem solid ${palette['border-line']};
    }
    ${unRead &&
    css`
      &::before {
        content: '';
        background-color: ${readableBackground};
        position: absolute;
        inset: 0;
        height: calc(100% + 0.0625rem);
        top: -0.0625rem;
        width: 0.125rem;
      }
    `}
  `;
});
StyledEmailInConversation.defaultProps = defaultThemeProp;

const StyledUnreadIndicator = styled.span<{ isVisible: boolean }>(
  ({
    theme: {
      base: { palette }
    },
    isVisible
  }) => {
    const readableBackground = readableHue(palette['brand-primary'], palette['primary-background']);

    const diameter = '0.375rem';

    return css`
      width: ${isVisible ? diameter : 0};
      margin: 0 ${isVisible ? 0 : '0.188rem'};
      height: ${diameter};
      display: inline-block;
      border-radius: 50%;
      background: ${readableBackground};
      position: relative;
      top: 0.375rem;
      align-self: flex-start;
    `;
  }
);
StyledUnreadIndicator.defaultProps = defaultThemeProp;

const StyledConversationButton = styled.button(
  ({
    theme: {
      base: { spacing, palette }
    }
  }) => {
    const hoverColor = tryCatch(() =>
      mix(0.85, palette['primary-background'], palette['brand-primary'])
    );
    return css`
      border-width: 0;
      width: 100%;
      padding: ${spacing};
      background: ${palette['primary-background']};
      color: ${palette['foreground-color']};
      position: sticky;
      top: 0;
      z-index: 1;

      &[aria-expanded='true'] {
        border-bottom: 0.0625rem solid ${palette['border-line']};
      }

      &:focus {
        background: ${palette['secondary-background']};
        outline: none;
      }

      &:hover {
        background: ${hoverColor};
        outline: none;
      }

      & > ${StyledIcon} {
        align-self: flex-start;
      }
    `;
  }
);

StyledConversationButton.defaultProps = defaultThemeProp;

const EmailConversation: ForwardRefForwardPropsComponent<EmailConversationProps> = forwardRef(
  function EmailConversation(
    props: PropsWithoutRef<EmailConversationProps>,
    ref: EmailConversationProps['ref']
  ) {
    const {
      id,
      emails,
      from,
      to,
      unReadEmailCount,
      attachmentCount,
      timeStamp,
      isForwarded = false,
      isCollapsed = false,
      onCollapse,
      onExpand,
      undelivered,
      drafts,
      ...restProps
    } = props;
    const t = useI18n();
    const [collapsedState, setCollapsedState] = useState(isCollapsed);
    useAfterInitialEffect(() => {
      setCollapsedState(isCollapsed);
    }, [isCollapsed]);
    const onExpandCollapse = () => {
      setCollapsedState(!collapsedState);
    };

    const headerRef = useRef<HTMLDivElement>(null);
    const isSmallOrAbove = useBreakpoint('sm', {
      breakpointRef: headerRef,
      themeProp: 'content-width'
    });
    const { locale } = useConfiguration();
    const { rtl } = useDirection();

    const caretDirection = rtl ? 'caret-left' : 'caret-right';
    const generateRecipientList = useMemo(() => {
      const recipientElements = to.slice(0, 2).map((recipient, i) => {
        return `${recipient.shortName}${i < to.length - 1 ? ';' : ''} `;
      });
      if (to.length > 2) {
        return [...recipientElements, `+${to.length - 2} more`];
      }
      return recipientElements;
    }, [to]);

    const renderMetaData = (
      <>
        {collapsedState && typeof attachmentCount === 'number' && attachmentCount > 0 && (
          <Flex container={{ gap: 0.5, alignItems: 'center' }}>
            <Icon name='paper-clip' />
            <Count>{attachmentCount}</Count>
          </Flex>
        )}
        <Text variant='secondary' as={StyledEmailParticipantsText}>
          {`${getIntlDateTimeFormatFromCache(locale, { weekday: 'short', timeZone: 'UTC' }).format(new Date(timeStamp))}, `}
          <DateTimeDisplay variant='datetime' value={timeStamp} />
        </Text>
      </>
    );

    return (
      <StyledEmailConversation {...restProps} id={id} ref={ref}>
        <Flex
          as={StyledConversationButton}
          id={`conversation-heading${id}`}
          aria-controls={id}
          aria-expanded={!collapsedState}
          container={{ gap: 1, alignItems: 'center', pad: 1 }}
          onClick={onExpandCollapse}
          ref={headerRef}
        >
          <Icon name={collapsedState ? caretDirection : 'caret-down'} />
          <StyledUnreadIndicator isVisible={!!unReadEmailCount} />
          <Flex
            container={{
              wrap: 'nowrap',
              alignItems: 'start',
              direction: 'column'
            }}
            item={{
              grow: 1,
              shrink: 1
            }}
          >
            <Flex container={{ gap: 1 }}>
              <Text variant='primary' as={StyledEmailParticipantsText}>
                {`${isForwarded ? 'FW' : t('from')}: ${from.fullName}`}
              </Text>

              {(undelivered || drafts) && (
                <>
                  {undelivered && <Status variant='urgent'>{t('undelivered')}</Status>}
                  {/* Passing Infinity for count so the correct plural translation is chosen
                  along with an empty tokens array so that a count is not shown. */}
                  {drafts && (
                    <Status variant='pending'>{t('draft', [], { count: Infinity })}</Status>
                  )}
                </>
              )}
            </Flex>
            <Text variant='secondary' as={StyledEmailParticipantsText}>
              {`${t('to')}: `}
              {generateRecipientList}
            </Text>
            {!isSmallOrAbove && (
              <Flex container={{ gap: 1, alignItems: 'center', pad: [0.25, undefined, 0] }}>
                {renderMetaData}
              </Flex>
            )}
          </Flex>
          {isSmallOrAbove && (
            <Flex
              container={{
                gap: 4,
                alignItems: 'center'
              }}
              item={{
                shrink: 0
              }}
            >
              {renderMetaData}
            </Flex>
          )}
        </Flex>

        <ExpandCollapse
          as={Flex}
          container={{ direction: 'column' }}
          collapsed={collapsedState}
          onBeforeCollapse={onCollapse}
          onBeforeExpand={onExpand}
          role='region'
          aria-labelledby={`conversation-heading${id}`}
          id={`conversation-content${id}`}
        >
          {!!unReadEmailCount && (
            <Text as={StyledUnReadCount} id={`unread-emailCount-${id}`} variant='secondary'>
              {t('new_emails_count', [unReadEmailCount], { count: unReadEmailCount })}
            </Text>
          )}
          {emails &&
            emails.map(email => {
              return <StyledEmailInConversation key={email.id} {...email} />;
            })}
        </ExpandCollapse>
      </StyledEmailConversation>
    );
  }
);

export default EmailConversation;
